import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Paper,
  Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HistoryIcon from '@mui/icons-material/History';

interface AssignmentHistoryItem {
  id: number;
  vendor_firm: {
    id: number;
    name: string;
  };
  vendor_user?: {
    id: number;
    name: string;
  };
  is_active: boolean;
  assigned_at: string;
  unassigned_at?: string;
  assigned_by?: {
    id: number;
    name: string;
  };
  unassigned_by?: {
    id: number;
    name: string;
  };
  reassignment_reason?: string;
  due_date?: string;
}

interface VendorAssignmentHistoryProps {
  open: boolean;
  onClose: () => void;
  history: AssignmentHistoryItem[];
}

const VendorAssignmentHistory: React.FC<VendorAssignmentHistoryProps> = ({
  open,
  onClose,
  history,
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Vendor Assignment History</DialogTitle>
      <DialogContent>
        {history.length === 0 ? (
          <Typography color="text.secondary">No assignment history available</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {history.map((item, index) => (
              <Paper key={item.id} elevation={3} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {item.is_active ? (
                    <CheckCircleIcon color="success" />
                  ) : (
                    <HistoryIcon color="disabled" />
                  )}
                  <Typography variant="h6" component="span">
                    {item.vendor_firm.name}
                  </Typography>
                  {item.is_active && (
                    <Chip label="Current" color="success" size="small" />
                  )}
                </Box>

                <Divider sx={{ my: 1 }} />

                {item.vendor_user && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Assigned User:</strong> {item.vendor_user.name}
                  </Typography>
                )}

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Assigned:</strong> {formatDate(item.assigned_at)}
                  {item.assigned_by && ` by ${item.assigned_by.name}`}
                </Typography>

                {item.due_date && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Due Date:</strong> {new Date(item.due_date).toLocaleDateString()}
                  </Typography>
                )}

                {item.unassigned_at && (
                  <Typography variant="body2" color="error" gutterBottom>
                    <strong>Unassigned:</strong> {formatDate(item.unassigned_at)}
                    {item.unassigned_by && ` by ${item.unassigned_by.name}`}
                  </Typography>
                )}

                {item.reassignment_reason && (
                  <Box sx={{ mt: 2, p: 1.5, bgcolor: 'warning.light', borderRadius: 1 }}>
                    <Typography variant="caption" display="block" fontWeight="bold" gutterBottom>
                      Reason for {index === 0 ? 'Assignment' : 'Reassignment'}:
                    </Typography>
                    <Typography variant="body2">
                      {item.reassignment_reason}
                    </Typography>
                  </Box>
                )}
              </Paper>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default VendorAssignmentHistory;

